<template>
  <section
    ref="sectionEl"
    class="pills-tabs"
    :class="{
      'pills-tabs--secondary': secondaryColor,
      'pills-tabs--under-sections': pillsUnderSections,
    }"
  >
    <div class="pills-tabs__layout">
      <ul class="pills-tabs__pills" role="tablist">
        <PillsTabsSectionNav
          v-if="hasSections"
          :sections="groupedSections"
          :active-index="activeSectionIndex"
          @select="onSectionTabClick"
        />

        <template v-for="(item, i) in items" :key="item._uid">
          <li v-if="showSectionTitle(item, i)" class="pills-tabs__section-title" aria-hidden="true">
            {{ item.section_name }}
          </li>
          <li
            role="presentation"
            :class="{ 'pills-tabs__pill-item--hidden-mobile': hasSections && item.section_name !== activeSectionName }"
          >
            <button
              :id="`pill-${item._uid}`"
              :class="[
                'pills-tabs__pill',
                activeIndex === i && isAutoPlaying ? 'pills-tabs__pill--auto' : '',
                activeIndex === i && !isAutoPlaying ? 'pills-tabs__pill--selected' : '',
              ]"
              role="tab"
              :aria-selected="activeIndex === i"
              :aria-controls="`panel-${item._uid}`"
              @click="onPillClick(i)"
            >
              <span class="pills-tabs__pill-label">{{ item.label }}</span>
              <span
                v-if="activeIndex === i && isAutoPlaying"
                :key="`fill-${fillKey}`"
                class="pills-tabs__pill-fill"
                :style="{ '--duration': `${duration}s` }"
                aria-hidden="true"
              />
            </button>
          </li>
        </template>
      </ul>

      <div class="pills-tabs__content">
        <Transition name="pills-tabs-fade" mode="out-in">
          <div
            :id="`panel-${items[activeIndex]?._uid}`"
            :key="activeIndex"
            role="tabpanel"
            :aria-labelledby="`pill-${items[activeIndex]?._uid}`"
            class="pills-tabs__content-inner"
            tabindex="0"
          >
            <StoryblokComponent
              v-for="blok in items[activeIndex]?.content"
              :key="blok._uid"
              :blok="blok"
            />
          </div>
        </Transition>
      </div>
    </div>
  </section>
</template>

<script lang="ts" setup>
import type { SbBlokData } from '@storyblok/js'

export interface PillsTabItem {
  _uid: string
  label: string
  content: SbBlokData[]
  section_name?: string
}

interface Props {
  items?: PillsTabItem[]
  secondaryColor?: boolean
  pillsUnderSections?: boolean
  duration?: number
}

const props = withDefaults(defineProps<Props>(), {
  items: () => [],
  secondaryColor: false,
  pillsUnderSections: false,
  duration: 10,
})

const sectionEl = ref<HTMLElement | null>(null)
const activeIndex = ref(0)
const activeSectionIndex = ref(0)
const isAutoPlaying = ref(false)
const userStopped = ref(false)
const fillKey = ref(0)

let timer: ReturnType<typeof setTimeout> | null = null

const groupedSections = computed(() => {
  const groups: Array<{ name: string; items: Array<{ item: PillsTabItem; index: number }> }> = []
  props.items?.forEach((item, i) => {
    if (!item.section_name) return
    const last = groups[groups.length - 1]
    if (!last || last.name !== item.section_name) {
      groups.push({ name: item.section_name, items: [] })
    }
    groups[groups.length - 1]!.items.push({ item, index: i })
  })
  return groups
})

const hasSections = computed(
  () => props.pillsUnderSections && groupedSections.value.length > 0,
)

const activeSectionName = computed(
  () => hasSections.value ? groupedSections.value[activeSectionIndex.value]?.name : null,
)

function showSectionTitle(item: PillsTabItem, index: number): boolean {
  if (!item.section_name) return false
  return index === 0 || props.items?.[index - 1]?.section_name !== item.section_name
}

function onSectionTabClick(si: number) {
  activeSectionIndex.value = si
  const firstItemIndex = groupedSections.value[si]?.items[0]?.index
  if (firstItemIndex != null) {
    userStopped.value = true
    stopAutoPlay()
    activeIndex.value = firstItemIndex
  }
}

function advance() {
  if (!isAutoPlaying.value) return
  activeIndex.value = (activeIndex.value + 1) % (props.items?.length ?? 1)

  const newItem = props.items?.[activeIndex.value]
  if (newItem && newItem.section_name && hasSections.value) {
    const si = groupedSections.value.findIndex(s => s.name === newItem.section_name)
    if (si !== -1) activeSectionIndex.value = si
  }

  fillKey.value++
  timer = setTimeout(advance, props.duration * 1000)
}

function startAutoPlay() {
  if (isAutoPlaying.value || userStopped.value || (props.items?.length ?? 0) < 2) return
  isAutoPlaying.value = true
  fillKey.value++
  timer = setTimeout(advance, props.duration * 1000)
}

function stopAutoPlay() {
  isAutoPlaying.value = false
  if (timer !== null) {
    clearTimeout(timer)
    timer = null
  }
}

function onPillClick(index: number) {
  userStopped.value = true
  stopAutoPlay()
  activeIndex.value = index
}

onMounted(() => {
  const observer = new IntersectionObserver(
    entries => {
      if (entries[0]?.isIntersecting) startAutoPlay()
      else stopAutoPlay()
    },
    { threshold: 0.2 },
  )
  if (sectionEl.value) observer.observe(sectionEl.value)
})

onUnmounted(() => {
  stopAutoPlay()
})
</script>

<style lang="scss" scoped>
.pills-tabs {
  &__layout {
    display: flex;
    flex-direction: column;
    gap: 32px;

    @media (min-width: $breakpoint-lg) {
      flex-direction: row;
      align-items: flex-start;
      gap: 50px;
    }
  }

  &__content {
    flex: 1;
    min-width: 0;
  }

  &__pills {
    flex: 0 0 auto;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 8px;
    list-style: none;
    padding: 0;
    margin: 0;
    width: 100%;

    @media (min-width: $breakpoint-lg) {
      flex-direction: column;
      flex-wrap: nowrap;
      width: 260px;
    }
  }

  &--under-sections &__pills {
    @media (min-width: $breakpoint-lg) {
      flex-direction: row;
      flex-wrap: wrap;
      width: auto;
      max-width: 434px;
    }
  }

  &__section-title {
    width: 100%;
    font-size: var(--font-size-paragraph-m);
    font-weight: 700;
    color: var(--navy-800);
    margin-top: 32px;

    &:first-child {
      margin-top: 0;
    }
  }

  &__section-tabs-row + &__section-title {
    margin-top: 0;
  }

  &--under-sections &__section-title {
    display: none;

    @media (min-width: $breakpoint-lg) {
      display: block;
    }
  }

  &--under-sections &__pill-item--hidden-mobile {
    @media (max-width: #{$breakpoint-lg - 1}) {
      display: none;
    }
  }

  &--secondary &__pill:not(&__pill--auto):not(&__pill--selected) {
    background: var(--paper-100);
    border: 1px solid var(--paper-200);
    box-shadow: none;
  }
}

.pills-tabs__pill {
  position: relative;
  display: flex;
  align-items: center;
  width: fit-content;
  padding: 6px 12px;
  border-radius: 12px;
  border: none;
  background: var(--white);
  color: var(--cobalt-800);
  font-size: var(--font-size-paragraph-s);
  font-weight: 400;
  line-height: 1.4;
  text-align: left;
  cursor: pointer;
  overflow: hidden;
  @include shadow-small-soft;
  transition:
    background 0.2s ease,
    color 0.2s ease;

  &:hover:not(.pills-tabs__pill--auto):not(.pills-tabs__pill--selected) {
    background: var(--paper-300);
  }

  &--auto {
    background: var(--cobalt-600);
    color: var(--white);
  }

  &--selected {
    background: var(--cobalt-600);
    color: var(--white);
  }

  &:active {
    background: var(--cobalt-700) !important;
    color: var(--white);
  }

  &-label {
    position: relative;
    z-index: 1;
  }

  &-fill {
    position: absolute;
    inset: 0;
    background: var(--cobalt-700);
    transform-origin: left center;
    animation: pill-fill-progress var(--duration, 10s) linear forwards;
    z-index: 0;
  }
}

@keyframes pill-fill-progress {
  from { transform: scaleX(0); }
  to   { transform: scaleX(1); }
}

.pills-tabs-fade-enter-active,
.pills-tabs-fade-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}

.pills-tabs-fade-enter-from {
  opacity: 0;
  transform: translateY(8px);
}

.pills-tabs-fade-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}
</style>
